import BaseUISystem from '../BaseUISystem';
import BaseMenu from './BaseMenu';
import RelativePositionComponent from 'Component/Render/RelativePosition';
import Point from 'Utility/Point';
import { IGameState } from 'State/Game/IGameState';
import { DefaultQuestsPopupLayout } from 'Layouts/Quests/DefaultQuestsPopupLayout';
import { UIActions } from 'State/UI/UIActions';
import Game from 'Game';
import { ArrayHelper } from 'Utility/ArrayHelper';
import QuestItem from 'Prototype/UI/QuestItem';
import QuestHelper from 'Utility/QuestHelper';
import { PopupID } from 'State/UI/PopupID';
import { getLayout, GLayout } from 'Utility/layout/LayoutHelper';
import { createEventLayout, initButton, setEventQuestProgress, updateEventData } from 'System/UI/Menu/QuestsMenuHelper';
import { getEventLayout } from 'State/Events/EventHelper';
import { EventQuestPopupLayout } from 'Constants/Events/CurrentEvent';
import { EventStateHelper } from 'State/Events/EventStateHelper';
import { createSeasonPassLayout, handleQuestTabs, ISeasonPassRankLayout, isSeasonPassTabActive, switchToMissionTab, switchToSeasonPassTab } from './SeasonPassMenuHelper';
import { PlayerActions } from 'State/Player/PlayerActions';
import { registerGameObject, unregisterGameObject } from 'GameObject/GameObject';
import { FTUXStateHelper } from 'State/Game/FTUX/FTUXStateHelper';
import { FTUXType } from 'Player/PlayerProperty/Progress/FTUXType';
import { getCurrentTractorPosition } from '../SeasonPass/TractorHelper';
import { addDragonbonesResources } from 'Utility/Resource/ResourceHelper';
import { DRAGONBONES_PATH } from 'Constants/Dragonbones';
import { finishedSeasonPass } from 'State/Player/PlayerSeasonPassHelper';

export default class QuestsMenuHandler extends BaseMenu {
    public layout: GLayout<DefaultQuestsPopupLayout> | GLayout<EventQuestPopupLayout>;
    private questItems: QuestItem[] = [];
    public seasonPassRankLayout: ISeasonPassRankLayout;
    private lastPlayerRank: number;
    public collectableQuestsCount: number = 0;
    public hasCollectableEventQuest = false;

    constructor(uiSystem: BaseUISystem) {
        super(PopupID.QUEST_MENU, uiSystem, false);
    }

    public onCreate() {
        super.onCreate();

        const state = Game.store.getState();

        const eventsWithQuests = EventStateHelper.getEventsWithQuests(state);
        const validEvent = ArrayHelper.sortByHighestPriority(eventsWithQuests)[0];
        const eventLayout = validEvent ? getEventLayout(validEvent.type, PopupID.QUEST_MENU) : null;

        this.lastPlayerRank = getCurrentTractorPosition(state.playerState).rank + 1;

        this.layout = eventLayout ?? getLayout(DefaultQuestsPopupLayout);

        this.hasCollectableEventQuest = false;

        this.layout.root.addComponent(RelativePositionComponent);
        this.layout.root.getComponent(RelativePositionComponent).relativePosition = new Point(0.5, 0.5);
        this.element.addChild(this.layout.root);

        initButton(this.layout.subLayout.button, this.continue, this.cleanupManager);
        initButton(this.layout.subLayout.closeButton, this.close, this.cleanupManager);

        this.initializeSeasonPassGameObjects();

        this.layout.subLayout.quests.forEach((questObject: any, index: number) => {
            const questItem = new QuestItem({
                layout: this.layout.subLayout.quests[index],
                data: state.playerState.quests[index],
                questIndex: index,
                questsMenuHandler: this,
            });

            this.questItems.push(questItem);
            this.cleanupManager.add(() => {
                questItem.destroy();
                this.questItems = ArrayHelper.remove(this.questItems, questItem);
            });
        });

        if (eventLayout) {
            createEventLayout(state, this.layout as GLayout<EventQuestPopupLayout>, this.cleanupManager, this);
        }
    }

    public onShow(params: { autoStatus?: string, quests?: number[] }) {
        const state = Game.store.getState();
        this.collectableQuestsCount = 0;
        this.questItems.forEach((questItem, index) => {
            questItem.updateData(state.playerState.quests[index], state);
        });

        updateEventData(state, this.layout);

        if (params.autoStatus) {
            switch (params.autoStatus) {
                case 'quest_complete':
                    switchToMissionTab(this.layout as GLayout<EventQuestPopupLayout>);

                    const initialDelay = this.hasCollectableEventQuest ? finishedSeasonPass(state.playerState) ? 1500 : 2000 : 0;
                    const sortedQuests = [...new Set(params.quests)].sort((a, b) => a - b);
                    sortedQuests.forEach((questIndex, indexInArray) => {
                        this.questItems[questIndex].setCollectDelay(initialDelay + (indexInArray * 2500));
                    });
                    this.collectableQuestsCount = sortedQuests.length;
                    break;
            }
        }
    }

    private readonly close = () => {
        if (this.blockClose) return;
        UIActions.hidePopup(PopupID.QUEST_MENU);
    };

    private readonly continue = () => {
        if (this.blockClose) return;
        UIActions.hidePopup(PopupID.QUEST_MENU);
        if (isSeasonPassTabActive()) {
            PlayerActions.showLevelAutoFlowPopup({
                allowRandomizedLevelSelection: true,
                delay: 50,
            });
        }
    };

    public async preload() {
        const state = Game.store.getState();
        state.playerState.quests.forEach(quest => {
            QuestHelper.addTileIfNeeded(quest.type, quest.tier);
        });
        // If there is an active quest then preload the event quest assets
        // TODO: This should be dynamic like the layouts in some way
        const eventQuest = EventStateHelper.getActiveQuest(state);
        if (eventQuest) {
            this.resources.set('event-quest-atlas', 'resource/graphics/sprite_sheets/event_quests.json');
            if (eventQuest.type) {
                const eventPoolKey = QuestHelper.getQuestPoolKeyByEventType(eventQuest.eventType);
                QuestHelper.addTileIfNeeded(eventQuest.type, eventQuest.tier, eventPoolKey);
            }
        }

        addDragonbonesResources('filling_progress_bar', `${DRAGONBONES_PATH}/SeasonPass/`);
        addDragonbonesResources('premium_purchase', `${DRAGONBONES_PATH}/SeasonPass/`);
        addDragonbonesResources('premium_unlock', `${DRAGONBONES_PATH}/SeasonPass/`);
        addDragonbonesResources('lock_removed', `${DRAGONBONES_PATH}/SeasonPass/`);
        await Game.resource.load();

        await super.preload();
    }

    public handleStateChanged(state: IGameState): void {
        super.handleStateChanged(state);
        this.questItems.forEach(questItem => questItem.handleStateChanged(state));

        updateEventData(state, this.layout);
        setEventQuestProgress(state, this.layout);
    }

    private initializeSeasonPassGameObjects() {
        this.layout.subLayout.season_pass_board.name = 'season_pass_board';
        this.layout.subLayout.tabContainerFront.name = 'tabContainerFront';
        registerGameObject(this.layout.subLayout.season_pass_board);
        registerGameObject(this.layout.subLayout.tabContainerFront);
        this.cleanupManager.add(() => {
            unregisterGameObject(this.layout.subLayout.season_pass_board);
            unregisterGameObject(this.layout.subLayout.tabContainerFront);
        });
    }

    public get blockClose(): boolean {
        return Boolean(this.questItems.find((e: QuestItem) => e && e.isAnimating) || !FTUXStateHelper.isFtuxCompleted(Game.store.getState().playerState, FTUXType.QUESTS));
    }

    public checkCollectableQuests() {
        const state = Game.store.getState();
        this.collectableQuestsCount--;
        if (this.collectableQuestsCount <= 0 && getCurrentTractorPosition(state.playerState).rank + 1 > this.lastPlayerRank) {
            // switchToSeasonPassTab(this.layout as GLayout<EventQuestPopupLayout>, this.seasonPassRankLayout);
        }
    }
}
